import {
  createContext,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  useState,
} from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import { getBookings } from '../../services/apiService';
import {
  ACTIONS,
  columnDefs,
  defaultColDef,
  detailCellRendererParams,
} from './_utils/definitions-config';
import { HiOutlineRefresh, HiOutlinePlus } from 'react-icons/hi';
import CancelBooking from './_modals/CancelBooking';
import RescheduleSession from './_modals/RescheduleSession';
import UpdateSessionDateTime from './_modals/UpdateSessionDateTime';
import AssignCoach from './_modals/AssignCoach';
import ReplaceCoach from './_modals/ReplaceCoach';
import debounce from 'lodash.debounce';
import { Link } from 'react-router-dom';

import SessionQuestionsPopup from './_modals/SessionQuestionsPopup';
import ReactivateBooking from './_modals/ReactivateBookings';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';
import { AG_GRID_PAGESIZE, BOOKING_STAGES } from '../../constants';
import useDebounce from '../../extras/useDebounce';
import { fetchAllThemes } from '../../redux/actions/themeActions';
import useUrlFilters from '../../extras/useUrlFilters';


export const CustomContext = createContext();
export default function ManageBookings() {
  const {
    filters: urlFilters,
    setFilters: setUrlFilters,
    gridFilterModel,
    setGridFilterModel,
  } = useUrlFilters({
    defaultFilters: { search: '' },
    enableGridFilter: true,
  });

  const [searchString, setSearchString] = useState(urlFilters.search || '');
  const debouncedSearch = useDebounce(searchString);

  // Keep URL in sync when search input changes
  useEffect(() => {
    setUrlFilters({ search: debouncedSearch || '' });
  }, [debouncedSearch]);
  const [selectedAction, setSelectedAction] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showQuestionsPopup, setShowQuestionsPopup] = useState(null);
  const [gridApi, setGridApi] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  // const [gridColumnApi, setGridColumnApi] = useState(null);
  const dispatch = useDispatch();
  const gridRef = useRef();
  // Prevents onFilterChanged from writing back to URL when we programmatically
  // call setFilterModel (e.g. restoring URL filters on first render).
  const applyingUrlFilter = useRef(false);
  const { themes, subTopics } = useSelector(({ themeReducer }) => themeReducer);
  const fetchBookings = () => {
    // onGridReady?.(gridApi, true);
  };

  useEffect(() => {
    if (!themes.length || !subTopics.length) {
      dispatch(fetchAllThemes());
    }
  }, []);

  useEffect(() => {
    gridApi && onGridReady?.(gridApi);
  }, [debouncedSearch]);

  const refreshBookings = () => {
    setIsRefreshing(true);
    gridRef.current.api.onFilterChanged();
    setTimeout(() => {
      setIsRefreshing(false);
    }, 1000);
  };

  const actionModal = useMemo(() => {
    switch (selectedAction) {
      case ACTIONS.CANCEL_BOOKING:
        return (
          <CancelBooking
            booking={selectedBooking}
            refreshData={fetchBookings}
          />
        );
      case ACTIONS.RESCHEDULE_SESSION:
        return (
          <RescheduleSession
            booking={selectedBooking}
            refreshData={fetchBookings}
          />
        );
      case ACTIONS.UPDATE_DATETIME:
        return (
          <UpdateSessionDateTime
            booking={selectedBooking}
            refreshData={fetchBookings}
          />
        );
      case ACTIONS.ASSIGN_COACH:
        return (
          <AssignCoach booking={selectedBooking} refreshData={fetchBookings} />
        );
      case ACTIONS.REPLACE_COACH:
        return (
          <ReplaceCoach booking={selectedBooking} refreshData={fetchBookings} />
        );
      case ACTIONS.REACTIVATE_BOOKING:
        return (
          <ReactivateBooking
            booking={selectedBooking}
            refreshData={fetchBookings}
          />
        );
      default:
        return null;
    }
  }, [selectedAction]);

  const getDateFilterObject = (data) => {
    return [
      moment(data?.dateFrom).toISOString(),
      moment(data.dateTo || data?.dateFrom)
        .add(24, 'hours')
        .toISOString(),
    ];
  };

  const getDataId = (selected, data) => {
    if (data.length) {
      const res = selected.map((item) => {
        const index = data.findIndex((itm) => itm.name === item);
        return data[index].id;
      });
      return res;
    }
  };

  const getStageValueText = (selected, data) => {
    const result = [];
    Object.entries(data).filter(
      ([key, val]) => selected.includes(val.text) && result.push(val.value)
    );
    return result;
  };

  const filterDataEnteries = (key, obj, data) => {
    switch (key) {
      case 'courseType':
      case 'status':
        obj[key] = data.values;
        break;
      case 'createdAt':
      case 'startTime':
      case 'conductedAt':
        obj[key] = getDateFilterObject(data);
        break;
      case 'themeName':
        obj.themeId = getDataId(data.values, themes);
        break;
      case 'subTopicName':
        obj.subTopicId = getDataId(data.values, subTopics);
        break;
      case 'stage':
        obj.stage = getStageValueText(data.values, BOOKING_STAGES);
        break;
      case 'noOfQuestions':
        obj[key] = data.filter;
        break;
      default:
        obj[key] = data;
    }
  };

  const getServerSideDatasource = () => {
    return {
      getRows: debounce((params) => {
        const filterData = {};
        const sortData = [];
        const paginationData = {};

        if (document?.getElementById('searchText')?.value?.length) {
          filterData.searchText = document.getElementById('searchText').value;
        }

        if (params.request) {
          const { startRow, endRow, filterModel, sortModel } = params.request;
          // sorting
          if (sortModel.length) {
            sortData.push([
              sortModel[0].colId,
              sortModel[0].sort.toUpperCase(),
            ]);
          }

          const filterKeys = Object.keys(filterModel);
          if (filterKeys.length) {
            filterKeys.forEach((key, index) => {
              filterDataEnteries(key, filterData, filterModel[key]);
            });
          }

          // pagination
          if (endRow) {
            paginationData.pageSize = AG_GRID_PAGESIZE;
            paginationData.pageNo = startRow / AG_GRID_PAGESIZE;
          }
        }
        const computedData = {
          ...filterData,
          ...(Object.keys(sortData).length && { order: sortData }),
          ...paginationData,
        };

        const payload = {
          includeStudent: true,
          includeCoach: true,
          includeExclusiveCoach: true,
          ...computedData,
        };

        getBookings(payload)
          .then((res) => {
            params.successCallback(res.items, res.count);
          })
          .catch((error) => {
            console.error(error);
            params.failCallback();
          });
      }, 1500),
    };
  };
  const onFirstDataRendered = (params) => {
    if (gridFilterModel) {
      applyingUrlFilter.current = true;
      params.api.setFilterModel(gridFilterModel);
    }
  };

  const onFilterChanged = (params) => {
    if (applyingUrlFilter.current) {
      applyingUrlFilter.current = false;
      return;
    }
    const filterModel = params.api.getFilterModel();
    setGridFilterModel(filterModel);
  };

  const onGridReady = useCallback(
    (params, server) => {
      const datasource = getServerSideDatasource();
      params.api.setServerSideDatasource(datasource);
      setGridApi(params);
    },
    [themes]
  );

  return (
    <CustomContext.Provider
      value={{ setSelectedAction, setSelectedBooking, setShowQuestionsPopup }}
    >
      <div>
        {/* Page header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '16px 0' }}>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0 }}>Manage Bookings</h1>
            <p style={{ fontSize: 13, color: '#6B7280', margin: '4px 0 0' }}>View and manage all coaching session bookings</p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button
                onClick={() => refreshBookings()}
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#374151',
                  backgroundColor: '#fff',
                  border: '1px solid #E5E7EB',
                  borderRadius: 4,
                  padding: '8px 16px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <HiOutlineRefresh size={14} className={isRefreshing ? 'animate-spin' : ''} />
                Refresh
              </button>
              <Link
                to='/manage-bookings/add-new-booking'
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#fff',
                  backgroundColor: 'var(--primary-main, #4F46E5)',
                  border: 'none',
                  borderRadius: 4,
                  padding: '8px 16px',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(79,70,229,0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  textDecoration: 'none',
                }}
              >
                <HiOutlinePlus size={14} /> Add New Booking
              </Link>
          </div>
        </div>

        {/* Search bar */}
        <div style={{ padding: '0 0 16px' }}>
          <input
            id='searchText'
            type='text'
            placeholder='Search bookings...'
            value={searchString || ''}
            onChange={(e) => setSearchString(e.target.value)}
            style={{
              width: '100%',
              maxWidth: 360,
              padding: '9px 14px',
              fontSize: 13,
              color: 'var(--text-primary, #111827)',
              backgroundColor: '#fff',
              border: '1px solid var(--border-light, #E5E7EB)',
              borderRadius: 8,
              outline: 'none',
              transition: 'border-color 150ms ease, box-shadow 150ms ease',
            }}
            onFocus={(e) => {
              e.target.style.borderColor = 'var(--primary-main, #4F46E5)';
              e.target.style.boxShadow = '0 0 0 3px rgba(79,70,229,0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'var(--border-light, #E5E7EB)';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>

        {/* Table */}
        <div
          style={{
            backgroundColor: 'var(--card-background, #fff)',
            borderRadius: 16,
            border: '1px solid var(--border-light, rgba(0,0,0,0.07))',
            boxShadow: 'var(--shadow-sm, 0 1px 6px rgba(0,0,0,0.05))',
            overflow: 'hidden',
          }}
        >
          <div className='ag-theme-alpine' style={{ width: '100%' }}>
            <AgGridReact
              ref={gridRef}
              columnDefs={columnDefs(themes, subTopics)}
              defaultColDef={defaultColDef}
              tooltipShowDelay={0}
              tooltipHideDelay={2000}
              onGridReady={onGridReady}
              domLayout='autoHeight'
              rowModelType='serverSide'
              rowHeight={50}
              serverSideStoreType='partial'
              animateRows={true}
              masterDetail={true}
              detailCellRendererParams={detailCellRendererParams}
              serverSideInfiniteScroll={true}
              pagination={true}
              onFirstDataRendered={onFirstDataRendered}
              onFilterChanged={onFilterChanged}
              paginationPageSize={AG_GRID_PAGESIZE}
              cacheBlockSize={AG_GRID_PAGESIZE}
            />
          </div>
        </div>

        {actionModal}

        {showQuestionsPopup && (
          <SessionQuestionsPopup
            booking={showQuestionsPopup}
            close={() => setShowQuestionsPopup(null)}
          />
        )}
      </div>
    </CustomContext.Provider>
  );
}
